import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { ErrorState } from '@bb/ui';
import { ApiError, type AdminApi, type VariantStatsBasis, type VariantStatsQuery, type VariantStatsView } from '../../../api/client.ts';
import { periodProblem, type Period } from '../period.ts';
import { VariantStatsTable } from './VariantStatsTable.tsx';
import { BASIS_LABELS, MDE_OPTIONS, conversionLabel, formatPercent, verdictText } from './format.ts';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

export interface VariantStatsPanelProps {
  environmentId: string;
  object: { type: 'campaign' | 'canvas'; id: string };
  /** The page's period: the report can narrow to it; by default it covers the whole history. */
  period?: Period;
  heading?: 'h2' | 'h3';
}

/** The method in words with its parameters (the API's explanation and `method`), folded by default. */
function MethodExplanation({ data }: { data: VariantStatsView }) {
  const m = data.method;
  return (
    <details className="variant-method">
      <summary>How this is calculated</summary>
      <p>{data.explanation.en}</p>
      <dl className="facts">
        <dt>Test</dt>
        <dd>Two-proportion z-test against control, Holm correction over {m.comparisons === 1 ? '1 comparison' : `${m.comparisons} comparisons`}</dd>
        <dt>Interval</dt>
        <dd>95 % Wilson</dd>
        <dt>Significance level</dt>
        <dd>α = {m.alpha}</dd>
        <dt>Power</dt>
        <dd>{formatPercent(m.power, 0)}</dd>
        <dt>Minimum detectable effect</dt>
        <dd>{formatPercent(m.mde, 0)} of the control rate</dd>
        <dt>Control rate</dt>
        <dd>{data.baselineRate === null ? '—' : formatPercent(data.baselineRate)}</dd>
        <dt>Minimum sample per group</dt>
        <dd>{data.requiredPerVariant === null ? 'Cannot be estimated yet' : data.requiredPerVariant.toLocaleString('en-US')}</dd>
      </dl>
      <p className="hint">«Confidence» is Φ(z), as Braze shows it: one-sided and uncorrected, for reference only. Decisions use the Holm-adjusted p-value.</p>
    </details>
  );
}

/**
 * Variants against control (BB-136, AN-004/AN-006) for a campaign or a Canvas: each group's conversion rate with a 95 %
 * Wilson interval, uplift, p-values before and after Holm correction, the sample each group needs for the chosen
 * minimum detectable effect, the verdict and why there is no winner yet. Basis, MDE, conversion and period are
 * options of the report; without control there is no uplift and no winner (DEC-017).
 */
export function VariantStatsPanel({ environmentId, object, period, heading = 'h2' }: VariantStatsPanelProps) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const [basis, setBasis] = useState<VariantStatsBasis>('assigned');
  const [mde, setMde] = useState(0.1);
  const [conversionId, setConversionId] = useState<string | undefined>(undefined);
  const [scope, setScope] = useState<'all' | 'period'>('all');
  const usePeriod = scope === 'period' && period !== undefined;
  const validPeriod = !usePeriod || periodProblem(period) === null;
  const query: VariantStatsQuery = { basis, mde, ...(conversionId ? { conversionId } : {}), ...(usePeriod ? { from: period.from, to: period.to } : {}) };
  const stats = useQuery({
    queryKey: ['variant-stats', environmentId, object.type, object.id, query],
    queryFn: () => (object.type === 'campaign' ? api.campaignVariantStats(environmentId, object.id, query) : api.canvasVariantStats(environmentId, object.id, query)),
    enabled: validPeriod,
    placeholderData: keepPreviousData,
  });
  const data = stats.data;
  const Heading = heading;
  return (
    <section className="panel variant-stats-panel" aria-label="Variant statistics">
      <Heading>Variants and control</Heading>
      <div className="toolbar" role="group" aria-label="Variant statistics options">
        <fieldset className="period-picker">
          <legend>Count users</legend>
          {(Object.keys(BASIS_LABELS) as VariantStatsBasis[]).map((b) => (
            <label key={b}>
              <input type="radio" name={`variant-basis-${object.id}`} value={b} checked={basis === b} onChange={() => setBasis(b)} /> {BASIS_LABELS[b]}
            </label>
          ))}
        </fieldset>
        <label>
          Minimum detectable effect{' '}
          <select value={String(mde)} onChange={(e) => setMde(Number(e.target.value))}>
            {MDE_OPTIONS.map((v) => (
              <option key={v} value={String(v)}>
                {formatPercent(v, 0)}
              </option>
            ))}
          </select>
        </label>
        {data && data.conversions.length > 1 && (
          <label>
            Conversion{' '}
            <select value={conversionId ?? data.conversion.id} onChange={(e) => setConversionId(e.target.value)}>
              {data.conversions.map((c) => (
                <option key={c.id} value={c.id}>
                  {conversionLabel(c)}
                </option>
              ))}
            </select>
          </label>
        )}
        {period && (
          <label>
            Period{' '}
            <select value={scope} onChange={(e) => setScope(e.target.value === 'period' ? 'period' : 'all')}>
              <option value="all">Whole history</option>
              <option value="period">
                {period.from} – {period.to}
              </option>
            </select>
          </label>
        )}
      </div>
      {!validPeriod && <p className="hint">Fix the period above to narrow the statistics to it.</p>}
      {stats.isError ? (
        <ErrorState title="Variant statistics could not be loaded" message={errorText(stats.error)} {...(stats.error instanceof ApiError ? { code: stats.error.code } : {})} onRetry={() => void stats.refetch()} />
      ) : !data ? (
        validPeriod && <p aria-busy="true">Loading variant statistics…</p>
      ) : (
        <>
          <p className="hint">
            Conversion: {conversionLabel(data.conversion)}. {data.from && data.to ? `Users assigned from ${data.from} to ${data.to}.` : 'All users ever assigned.'} Test users are left out.
          </p>
          <p role="status" className={data.verdict === 'winner' ? 'notice notice--neutral variant-verdict variant-verdict--winner' : 'notice notice--neutral variant-verdict'}>
            {verdictText(data)}
          </p>
          {data.warnings.length > 0 && (
            <ul className="plain-list" aria-label="Warnings">
              {data.warnings.map((w) => (
                <li key={w.code} className="notice notice--warning">
                  {w.en}
                  {w.count !== undefined ? ` (${w.count.toLocaleString('en-US')} ${w.count === 1 ? 'user' : 'users'})` : ''}
                </li>
              ))}
            </ul>
          )}
          {data.rows.length > 0 && <VariantStatsTable data={data} />}
          <MethodExplanation data={data} />
        </>
      )}
    </section>
  );
}
